import {
  AutoCompleteUncontrolled,
  ISelectType,
  OverflowTooltip,
  SearchInput,
  Table,
  useDebounce,
  useFetch,
} from "@4uhub/lib4uhub";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Chip } from "@mui/material";
import MainApi from "../../../../services/mainApi.service";
import { IIntegrationRules } from "../../../../models/integration-rule";
import { useCallback, useEffect, useMemo, useState } from "react";
import { busAutomationList } from "../../../../services/busAutomation.service";
import { dateOnlyStringToNewDate } from "../../../../utils/conversions";

const INTEGRATION_RULE_ROUTE =
  process.env.REACT_APP_MAIN + "/api/v1/4uBusIntegrationRule/";
const integrationRuleService = new MainApi<IIntegrationRules>(
  INTEGRATION_RULE_ROUTE,
);

const translationPath = "page.4uBus.integrationRule.";

const IntegrationRulePage = () => {
  const navigate = useNavigate();

  const [automations, setAutomations] = useState<ISelectType[]>([]);

  const [searchParams, setSearchParams] = useSearchParams();

  const { t } = useTranslation();

  const { sendRequest } = useFetch(busAutomationList);

  const [selectedAutomation, setSelectedAutomation] =
    useState<ISelectType | null>(null);

  const columns: GridColDef<IIntegrationRules>[] = [
    {
      field: "name",
      headerName: t(translationPath + "name") || "Name",
    },
    {
      field: "description",
      headerName: t(translationPath + "description") || "Description",
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
          <OverflowTooltip
            text={params.value}
            tooltip={params.value}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
    {
      field: "_4uBusAutomation",
      headerName: t(translationPath + "u_bus_automation") || "4uBus Automation",
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
          <OverflowTooltip
            text={params.value.name}
            tooltip={params.value.name}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
    {
      field: "iProcesse",
      headerName: t(translationPath + "i_processe") || "Process",
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
          <OverflowTooltip
            text={params.value.name}
            tooltip={params.value.name}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
    {
      field: "startDate",
      headerName: t(translationPath + "start_date") || "Start Date",
      renderCell: ({ row: { startDate } }) => {
        if (startDate && startDate.length > 0) {
          const date = dateOnlyStringToNewDate(startDate);
          return (
            <OverflowTooltip
              text={date.toLocaleDateString("pt-BR")}
              tooltip={date.toLocaleDateString("pt-BR")}
              textTransform="none"
              variant="body2"
            />
          );
        }
        return "-";
      },
    },
    {
      field: "endDate",
      headerName: t(translationPath + "end_date") || "End Date",
      renderCell: ({ row: { endDate } }) => {
        if (endDate && endDate.length > 0) {
          const date = dateOnlyStringToNewDate(endDate);
          return (
            <OverflowTooltip
              text={date.toLocaleDateString("pt-BR")}
              tooltip={date.toLocaleDateString("pt-BR")}
              textTransform="none"
              variant="body2"
            />
          );
        }
        return "-";
      },
    },
    {
      field: "enable",
      headerName: t(translationPath + "active") || "Enable",
      maxWidth: 90,
      renderCell: (params) => {
        return (
          <Chip
            size="small"
            label={
              params.value
                ? t(translationPath + "active")
                : t(translationPath + "inactive")
            }
            color={params.value ? "success" : "error"}
          />
        );
      },
    },
  ];

  const changeSearchHandler = useCallback(
    (value: string | null) => {
      const textSearch = (value ?? "").trim();

      const newParams = Object.fromEntries(searchParams.entries());

      if (textSearch) {
        newParams.searchByRule = textSearch;
      } else {
        delete newParams.searchByRule;
      }

      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const changeHandler = useCallback(
    (value: ISelectType | null) => {
      setSelectedAutomation(value);

      const newParams = Object.fromEntries(searchParams.entries());

      if (value?.id) {
        newParams.ruleSelectedAutomationId = value.id;
      } else {
        delete newParams.ruleSelectedAutomationId;
      }

      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleAdd = () => {
    navigate("new");
  };

  const handleRowClick = (params: GridRowParams<IIntegrationRules>) => {
    navigate(`${params.id.toString()}/edit`);
  };

  const text = useMemo(
    () => searchParams.get("searchByRule") ?? "",
    [searchParams],
  );

  const selectedAutomationId = useMemo(
    () => searchParams.get("ruleSelectedAutomationId") ?? "",
    [searchParams],
  );

  const searchBy = useDebounce(text, 500);

  const props = useMemo(() => {
    return {
      _4uBusAutomationId: selectedAutomationId || undefined,
      SearchBy: searchBy.length > 0 ? searchBy : undefined,
    };
  }, [selectedAutomationId, searchBy]);

  const uBusAutomationParams = useMemo(
    () => ({
      uBusAutomationCodeListRemove: [1],
    }),
    [],
  );

  const fetchAutomations = useCallback(async () => {
    const { data, success } = await sendRequest(uBusAutomationParams);
    if (data && success) {
      setAutomations(data);

      const savedId = searchParams.get("ruleSelectedAutomationId");
      if (savedId) {
        const found = data.find((opt) => opt.id === savedId);
        if (found) setSelectedAutomation(found);
      }
    }
  }, [sendRequest, searchParams, uBusAutomationParams]);

  useEffect(() => {
    fetchAutomations();
  }, [fetchAutomations]);

  return (
    <Table<IIntegrationRules>
      showDefaultMacroFilters={false}
      service={integrationRuleService}
      columns={columns}
      title={t(translationPath + "integration_rules")}
      onAdd={handleAdd}
      onRowClick={handleRowClick}
      serviceProps={props}
      searchInputPlaceHolder={t(translationPath + "search")}
      macroFilters={() => (
        <>
          <Box
            sx={{
              width: "30%",
              minWidth: "450px",
            }}
          >
            <SearchInput
              onSearch={changeSearchHandler}
              placeholder={t(translationPath + "search")}
              focus={false}
              cleanButton
              defaultValue={text}
              fireOnMount={false}
            />
          </Box>
          <AutoCompleteUncontrolled
            label={t(translationPath + "automation") || "Automation"}
            onValueSelected={changeHandler}
            value={selectedAutomation}
            options={automations}
            clearable={true}
            size="small"
            fullWidth
            sx={{ minWidth: 300 }}
          />
        </>
      )}
      addButtonLabel={
        t(translationPath + "add_integration_rule") || "Add Integration Rule"
      }
      confirmOptions={{
        title: t(translationPath + "confirm.title"),
        description: t(translationPath + "confirm.description"),
        confirmationButtonProps: {
          variant: "contained",
        },
      }}
    />
  );
};

export default IntegrationRulePage;
